import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FwDialogService } from '@flywheel-io/vision';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { BehaviorSubject, Observable, of, Subject as RxSubject, throwError, zip } from 'rxjs';
import { catchError, filter, first, map, mapTo, switchMap, tap } from 'rxjs/operators';

import { AddToCollectionDialog } from './add-to-collection-dialog/add-to-collection-dialog.component';
import { CachingService } from '../caching.service';
import { ConfigService } from '../config.service';
import { FileDownloadDialog } from '../containers/file-download-dialog/file-download-dialog.component';
import { ApiParams, CoreApiService, queryAll, WriteResult } from '../core-api.service';
import { CreateSessionDialog } from './create-session-dialog/create-session-dialog.component';
import { CreateSubjectDialog } from './create-subject-dialog/create-subject-dialog.component';
import { ConfirmDialog, DialogService } from '../dialogs';
import { DeleteDialogComponent, DeleteDialogResult } from '../dialogs/delete-dialog/delete-dialog.component';
import { ErrorDialog } from '../dialogs/error-dialog/error-dialog.component';
import { GearService } from '../gears/gear.service';
import { LoggingService } from '../logging.service';
import { MoveAcquisitionsToSessionDialog } from './move-acquisitions-to-session/move-acquisitions-to-session-dialog.component';
import { MoveSessionsToProjectDialog } from './move-sessions-to-project/move-sessions-to-project-dialog.component';
import { MoveSessionsToSubjectDialog } from './move-sessions-to-subject/move-sessions-to-subject-dialog.component';
import { NotificationService } from '../notification.service';
import { ProfileService } from '../profile/profile.service';
import { ProjectsService } from '../projects/projects.service';
import { pluralize } from '../shared/container.utils';
import { formatDate, timestampToLocaltime } from '../shared/date.utils';
import { ContainerType } from '../shared/enums/container-type.enum';
import { PermissionAction } from '../shared/enums/permission-action.enum';
import { RequestState } from '../shared/enums/request-state.enum';
import { getFileClassification, getOriginInfo, JoinOrigin, OriginInfo } from '../shared/file.utils';
import { Acquisition } from '../shared/models/acquisition.model';
import { DownloadNode } from '../shared/models/download.model';
import { File } from '../shared/models/file.model';
import { Paginated } from '../shared/models/paginated.model';
import { SearchQuery, SearchReturnType } from '../shared/models/search/search-query.model';
import { Session } from '../shared/models/session.model';
import { Subject } from '../shared/models/subject.model';
import { TreeQuery } from '../shared/models/tree-query.model';
import { firstValueFrom } from '../shared/observable.utils';
import { AcquisitionUploadDialog } from '../upload/acquisition-upload/acquisition-upload-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class SessionService extends CachingService {
  private sessions = {
    state: RequestState.Empty,
    subject: new RxSubject<Paginated<Session[]>>(),
  }
  readonly sessions$ = this.sessions.subject.asObservable()

  private subjects = {
    state: RequestState.Empty,
    subject: new RxSubject<Paginated<Subject[]>>(),
  }
  readonly subjects$ = this.subjects.subject.asObservable()

  readonly acquisitionChange$ = new RxSubject<null>()
  readonly checkedSessions$ = new BehaviorSubject<Map<string, SessionCheckedStatus>>(new Map())
  readonly checkedSubjects$ = new BehaviorSubject<Map<string, SubjectCheckedStatus>>(new Map())
  readonly sessionDeleted$ = new RxSubject<string>() // session IDs
  readonly subjectDeleted$ = new RxSubject<string>() // subject IDs
  readonly showTimezone$ = new BehaviorSubject<boolean>(false);

  private lastSessionPageParams: ApiParams = null; // This enables us to "reload" the current paged view
  public lastSessionDetailsTab = 'data';

  private fileClassifications = new WeakMap<File, string>()
  private fileOrigins = new WeakMap<File, OriginInfo>()

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private fwDialogService: FwDialogService,
    private coreApi: CoreApiService,
    private gearService: GearService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projects: ProjectsService,
    // private oldRouter: UIRouterState,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    super();
    this.profileService.settings$.pipe(map(settings => settings.originalTimezones)).subscribe(this.showTimezone$);
  }

  /**
   * @deprecated track session list state in component
   */
  getSessionPage(params: ApiParams): Observable<Paginated<Session[]>> {
    this.lastSessionPageParams = params;

    this.profileService.exhaustiveMode$.pipe(first()).subscribe(exhaustive => {
      const queryParams = omit(params, ['projectId', 'groupId', 'collectionId']);
      if (exhaustive) {
        queryParams.exhaustive = true;
      }

      const filters = params.filter ? (params.filter as string).split(',') : [];
      if (params.projectId) {
        filters.push(`project=${params.projectId}`);
      } else if (params.groupId) {
        filters.push(`group=${params.groupId}`);
      }
      if (filters.length > 0) {
        queryParams.filter = filters.join(',');
      }

      let query$ = this.coreApi.sessions.query(queryParams);
      if (params.collectionId) {
        query$ = this.coreApi.collections.sessions(params.collectionId as string, queryParams);
      }

      this.sessions.state = RequestState.Fetching;
      query$.subscribe({
        next: sessions => {
          this.sessions.state = RequestState.Loaded;
          this.sessions.subject.next(sessions);
        },
        error: error => {
          this.sessions.state = RequestState.Loaded;
          this.error.next(error);
        },
      });
    });
    return this.sessions$;
  }

  querySessions(params: ApiParams): Observable<Paginated<Session[]>> {
    return this.profileService.exhaustiveMode$.pipe(
      switchMap(exhaustive => {
        const queryParams = omit(params, ['projectId']);
        if (exhaustive) {
          queryParams.exhaustive = true;
        }
        const filters = params.filter ? (params.filter as string).split(',') : [];
        if (params.projectId) {
          queryParams.filter = [...filters, `project=${params.projectId}`].join(',');
        }
        let query$ = this.coreApi.sessions.query(queryParams);
        if (params.collectionId) {
          query$ = this.coreApi.collections.sessions(params.collectionId as string, queryParams);
        }
        return query$;
      }),
    );
  }

  getSession(sessionId: string): Observable<Session> {
    return this.coreApi.sessions.get(sessionId);
  }

  /**
   * @deprecated track subject list state in component
   */
  getSubjectPage(body: TreeQuery, params: ApiParams): Observable<Paginated<Subject[]>> {
    this.subjects.state = RequestState.Fetching;
    this.querySubjects(body, params).subscribe({
      next: subjects => {
        this.subjects.state = RequestState.Loaded;
        this.subjects.subject.next(subjects);
      },
      error: error => {
        this.subjects.state = RequestState.Loaded;
        this.error.next(error);
      },
    });
    return this.subjects$;
  }

  querySubjects(body: TreeQuery, params: ApiParams): Observable<Paginated<Subject[]>> {
    return this.profileService.exhaustiveMode$.pipe(
      switchMap(exhaustive => {
        const queryParams = omit(params, ['projectId']);
        if (exhaustive) {
          queryParams.exhaustive = true;
        }
        const filters = params.filter ? (params.filter as string).split(',') : [];
        if (params.projectId) {
          queryParams.filter = [...filters, `project=${params.projectId}`].join(',');
        }
        return this.coreApi.tree.query<Subject>(body, queryParams);
      }),
    );
  }

  /**
   * Cache classification strings in a WeakMap
   */
  getFileClassification(file: File): string {
    if (!this.fileClassifications.has(file)) {
      this.fileClassifications.set(file, getFileClassification(file));
    }
    return this.fileClassifications.get(file);
  }

  /**
   * Cache origin info in a WeakMap
   */
  getFileOrigin(file: File, origins: JoinOrigin): OriginInfo {
    if (!this.fileOrigins.has(file)) {
      this.fileOrigins.set(file, getOriginInfo(file, origins));
    }
    return this.fileOrigins.get(file);
  }

  getCheckedSubjects(): Map<string, SubjectCheckedStatus> {
    return this.checkedSubjects$.value;
  }

  getCheckedSessions(): Map<string, SessionCheckedStatus> {
    return this.checkedSessions$.value;
  }

  clearCheckedSubjects(): void {
    this.checkedSubjects$.next(new Map());
  }

  clearCheckedSessions(): void {
    this.checkedSessions$.next(new Map());
  }

  checkSubjects(subjectIds: string[], checked?: boolean): void {
    const checkedSubjects = this.getCheckedSubjects();
    for (const subjectId of subjectIds) {
      const status = checkedSubjects.get(subjectId) || {
        _id: subjectId,
        checked: false,
        indeterminate: false,
      } as SubjectCheckedStatus;
      status.checked = typeof checked === 'boolean' ? checked : !status.checked;
      checkedSubjects.set(subjectId, status);
    }
    this.checkedSubjects$.next(checkedSubjects);
  }

  checkSessions(sessionIds: string[], checked?: boolean, { clearAcquisitions = false } = {}): void {
    const checkedSessions = this.getCheckedSessions();
    for (const sessionId of sessionIds) {
      const status = checkedSessions.get(sessionId) || {
        _id: sessionId,
        checked: false,
        indeterminate: false,
        acquisitions: new Set(),
      };
      checked = typeof checked === 'boolean' ? checked : !status.checked;
      if (clearAcquisitions || status.checked !== checked) {
        // if we transition, clear individual acquisition selections
        status.acquisitions.clear();
        status.indeterminate = false;
      }
      status.checked = checked;
      checkedSessions.set(sessionId, status);
    }
    this.checkedSessions$.next(checkedSessions);
  }

  checkAcquisitions(session: Pick<Session, '_id' | 'acquisitions'>, acquisitionIds: string[]): void {
    return this.checkAcquisitionsBulk([{ session, acquisitionIds }]);
  }

  checkAcquisitionsBulk(updates: AcquisitionUpdate[]): void {
    const checkedSessions = this.getCheckedSessions();
    for (const { session, acquisitionIds } of updates) {
      const status = checkedSessions.get(session._id) || {
        _id: session._id,
        checked: false,
        indeterminate: false,
        acquisitions: new Set(),
      };
      status.checked = acquisitionIds.length > 0;
      status.indeterminate = status.checked;
      if (session.acquisitions instanceof Array) {
        status.indeterminate = status.checked && acquisitionIds.length < session.acquisitions.length;
      }
      status.acquisitions = new Set(acquisitionIds);
      checkedSessions.set(session._id, status);
    }
    this.checkedSessions$.next(checkedSessions);
  }

  async createSubject(projectId?: string): Promise<{ _id: string }> {
    try {
      return await firstValueFrom(
        this.dialog.open(CreateSubjectDialog, {
          data: {
            projectId,
          },
        }).afterClosed() as Observable<{ _id: string }>
      );
    } catch (error) {
      this.logger.error(error);
      return Promise.resolve(null);
    }
  }

  async createSession(projectId?: string): Promise<Partial<Session>> {
    try {
      return await firstValueFrom(
        this.dialog.open(CreateSessionDialog, {
          data: {
            projectId,
          },
        }).afterClosed()
      );
    } catch (error) {
      this.logger.error(error);
      return undefined;
    }
  }

  createAcquisition(session: Session): void {
    this.dialog.open(AcquisitionUploadDialog, {
      data: { session },
    }).afterClosed().subscribe((acquisition: Acquisition) => {
      if (acquisition?._id) {
        this.acquisitionChange$.next(null);
      }
    });
  }

  saveSession(sessionId: string, session: Partial<Session>): Observable<void> {
    return this.coreApi.sessions.put(sessionId, session).pipe(mapTo(null));
  }

  saveSubject(subjectId: string, subject: Partial<Subject>): Observable<void> {
    return this.coreApi.subjects.put(subjectId, subject).pipe(mapTo(null));
  }

  deleteSession(session: Pick<Session, '_id' | 'label'>): Observable<void> {
    return this.dialog.open(DeleteDialogComponent, {
      data: { content: `Are you sure you want to delete the session "${session.label || session._id}"?` },
    }).afterClosed().pipe(
      filter((proceed: DeleteDialogResult) => Boolean(proceed.delete)),
      switchMap(proceed => this.coreApi.containers.delete<Session>(session._id, proceed.deleteReason ? { delete_reason: proceed.deleteReason } : {})),
      tap(() => {
        //The route change causes the toast container to be destroyed, setTimeout allows for the notificaiton to show
        setTimeout(() => {
          this.notifications.success('session.delete.success');
        }, 0);
        const { sessionId, entityId } = this.activatedRoute.snapshot.params;
        if ([sessionId, entityId].includes(session._id)) {
          // TODO: router refresh thing
          // this.oldRouter.go('.', { sessionId: null, entityId: null }, { reload: true });
        }
        this.sessionDeleted$.next(session._id);
      }),
      catchError((error: HttpErrorResponse) => {
        const messages = {
          'error': 'session.delete.error',
          'original_data_present': 'session.delete.originalData',
          'analysis_conflict': 'session.delete.analysisConflict',
        };
        const reason = error.error?.reason;
        if (reason in messages) {
          this.notifications.error(messages[reason]);
        } else {
          this.logger.error(error);
          this.notifications.error(messages['error']);
        }
        return throwError(error);
      }),
      mapTo(null),
    );
  }

  deleteSubject(subject: Pick<Subject, '_id' | 'label'>): Observable<void> {
    return this.dialog.open(DeleteDialogComponent, {
      data: {
        content: `Deleting this subject will also delete all sessions associated with it.
        Are you sure you want to delete the subject "${subject.label || subject._id}"?`,
      },
    }).afterClosed().pipe(
      filter((proceed: DeleteDialogResult) => Boolean(proceed.delete)),
      switchMap(proceed => this.coreApi.containers.delete<Subject>(subject._id, proceed.deleteReason ? { delete_reason: proceed.deleteReason } : {})),
      tap(() => {
        //The route change causes the toast container to be destroyed, setTimeout allows for the notificaiton to show
        setTimeout(() => {
          this.notifications.success('session.deleteSubject.success');
        }, 0);
        const { entityId, projectTab, sessionId, subjectId } = this.activatedRoute.snapshot.params;
        if ([subjectId, entityId].includes(subject._id)
          || sessionId || (projectTab === 'sessions' && entityId)) { // assume one of the subject's sessions is selected
          // TODO: router refresh thing
          // this.oldRouter.go('.', { subjectId: null, entityId: null, sessionId: null }, { reload: true });
        }
        this.subjectDeleted$.next(subject._id);
      }),
      catchError((error: HttpErrorResponse) => {
        const messages = {
          'error': 'session.deleteSubject.error',
          'original_data_present': 'session.deleteSubject.originalData',
          'analysis_conflict': 'session.deleteSubject.analysisConflict',
        };
        const reason = error.error?.reason;
        if (reason in messages) {
          this.notifications.error(messages[reason]);
        } else {
          this.logger.error(error);
          this.notifications.error(messages['error']);
        }
        return throwError(error);
      }),
      mapTo(null),
    );
  }

  deleteContainers(data: ContainerDeletionCriteria[], params?: ApiParams): Promise<ContainerDeletionResults> {
    let successfulDeletions = 0;
    const errors: { [key: string]: Error } = {};
    const filteredData = data.filter(({ containers }) => containers.length > 0);
    return firstValueFrom(
      zip(filteredData.map(({ containers, updateKey }) => {
        const containerIds = containers.map(({ _id }) => _id);
        return (this.coreApi[updateKey].deleteBulk(containerIds, params) as Observable<{ deleted: number }>)
          .pipe(
            catchError(error => {
              let key = updateKey;
              if (error.status === 404) {
                key = '404';
              }
              errors[key] = error;
              return of({ deleted: 0 });
            }),
            tap((writeResult) => {
              successfulDeletions += writeResult.deleted;
            }),
          );
      })).pipe(map(() => { return { successfulDeletions, errors }; }),)
    );
  }

  deleteAllFromQuery(query: SearchQuery, params?: ApiParams): Promise<ContainerDeletionResults> {
    // FIXME: The api endpoints currently don't support queries so we can't use this yet
    let successfulDeletions = 0;
    let containerType = '';
    switch (query.return_type) {
      case SearchReturnType.Session:
        containerType = 'sessions';
        break;
      case SearchReturnType.Subject:
        containerType = 'subjects';
        break;
      case SearchReturnType.Acquisition:
        containerType = 'acquisitions';
        break;
      default:
        return null;
    }
    const errors: { [key: string]: Error[] } = {};
    return firstValueFrom(
      this.coreApi[containerType].deleteQuery(query.filters, params).pipe(
        catchError(error => {
          let key = 'error';
          if (error.data !== null && error.data.reason !== null) {
            key = error.data.reason;
          }
          else if (error.error !== null && error.error.reason !== null) {
            key = error.error.reason;
          }
          errors[key] = errors[key] || [];
          errors[key].push(error);
          return throwError(error);
        }),
        tap((writeResult: WriteResult) => {
          successfulDeletions += writeResult.deleted;
        }),
      ).pipe(map(() => { return { successfulDeletions, errors }; }))
    );
  }

  private async showErrorDialog(message: string): Promise<void> {
    const dialogRef = this.fwDialogService.open(ErrorDialog, {
      data: {
        contentText: message,
        title: 'Permissions Error',
        cancelButtonText: 'Close',
      },
    });

    await firstValueFrom(dialogRef.closed);
  }

  private async getAllowedContainers(metadata: ContainerMetadata, action: PermissionAction, containerType: ContainerType = ContainerType.Session): Promise<AllowedContainers> {
    const projectPermissions = await this.projects.projectPermissions$.pipe(first()).toPromise();
    const containerIds = new Set<string>();
    const acquisitionIds = new Set<string>();
    const forbiddenProjectIds = new Set<string>();
    const checkedContainers = containerType === ContainerType.Session ? this.getCheckedSessions() : this.getCheckedSubjects();
    for (const [containerId, checkedStatus] of checkedContainers) {
      if (!checkedStatus?.checked) {
        continue;
      }
      const projectId = metadata.get(containerId)?.project;
      if (!projectId) {
        continue;
      }
      if (!projectPermissions[projectId]?.includes(action)) {
        forbiddenProjectIds.add(projectId);
        continue;
      }
      if (checkedStatus.indeterminate) {
        for (const acquisitionId of (checkedStatus as SessionCheckedStatus).acquisitions ?? []) {
          acquisitionIds.add(acquisitionId);
        }
      } else {
        containerIds.add(containerId);
      }
    }
    const allowedContainers = {
      [pluralize(containerType)]: Array.from(containerIds),
      acquisitions: Array.from(acquisitionIds),
      forbiddenProjects: [],
    };
    if (forbiddenProjectIds.size > 0) {
      const allProjects = await this.projects.getProjects().pipe(first()).toPromise();
      allowedContainers.forbiddenProjects = Array.from(forbiddenProjectIds).map(projectId => {
        const project = allProjects.find(({ _id }) => _id === projectId);
        return project?.label || project?._id;
      });
    }
    return allowedContainers;
  }

  /**
   * Download files in checked sessions/acquisitions.
   * @param metadata - Map of all session _id to session label and project _id
   */
  async downloadCheckedFiles(metadata: ContainerMetadata, containerType?: ContainerType): Promise<void> {
    const { acquisitions = [], forbiddenProjects, sessions = [], subjects = [] } = await this.getAllowedContainers(metadata, PermissionAction.FilesDownload, containerType);
    if (forbiddenProjects.length > 0) {
      const confirmed = await this.dialog.open(ConfirmDialog, {
        data: { content: `You do not have permissions to download files from the following project(s): ${forbiddenProjects.join(', ')}, do you want to continue?` },
      }).afterClosed().toPromise();
      if (!confirmed) {
        return;
      }
    }
    const nodes: DownloadNode[] = [
      ...acquisitions.map(acquisitionId => ({ level: 'acquisition', _id: acquisitionId })),
      ...sessions.map(sessionId => ({ level: 'session', _id: sessionId })),
      ...subjects.map(subjectId => ({ level: 'subject', _id: subjectId })),
    ];
    this.dialog.open(FileDownloadDialog, {
      data: { requestBody: { nodes } },
    });
  }

  async runBatchGear(metadata: ContainerMetadata): Promise<void> {
    const { sessions, acquisitions, forbiddenProjects } = await this.getAllowedContainers(metadata, (
      this.config.isFeatureEnabled('job_prioritization')
        ? PermissionAction.JobsManage
        : PermissionAction.JobsRunCancel
    ));
    if (forbiddenProjects.length > 0) {
      const confirmed = await this.dialog.open(ConfirmDialog, {
        data: { content: `You do not have permissions to run gears on the following project(s): ${forbiddenProjects.join(', ')}. Do you want to continue?` },
      }).afterClosed().toPromise();
      if (!confirmed) {
        return;
      }
    }
    return this.gearService.runGear({
      batchMode: true,
      containers: [
        ...sessions.map(sessionId => ({ container_type: ContainerType.Session, _id: sessionId })),
        ...acquisitions.map(acqId => ({ container_type: ContainerType.Acquisition, _id: acqId })),
      ],
    });
  }

  async confirmDeleteChecked(metadata: ContainerMetadata): Promise<ContainerDeletionResults> {
    const { sessions, acquisitions, forbiddenProjects } = await this.getAllowedContainers(metadata, PermissionAction.ContainersDeleteHierarchy);
    let dialogContent = 'Are you sure you want to delete the selected data?';
    if (forbiddenProjects.length > 0) {
      dialogContent = 'You do not have permission to delete data from the following project(s): ' +
        forbiddenProjects.join(', ') + '. Do you want to proceed with deletion?';
    }
    const confirmResult = await this.dialog.open(DeleteDialogComponent, {
      data: { content: dialogContent },
    }).afterClosed().toPromise() as DeleteDialogResult;
    if (!confirmResult.delete) {
      return Promise.resolve(null);
    }
    const deletionResults = await this.deleteContainers([
      {
        containers: acquisitions.map(id => ({ _id: id })),
        updateKey: 'acquisitions',
      },
      {
        containers: sessions.map(id => ({ _id: id })),
        updateKey: 'sessions',
      },
    ], confirmResult.deleteReason ? { delete_reason: confirmResult.deleteReason } : {});
    // If a bulk deletion errors, successfulDeletions will be 0. Show success if we got a 404 as that means the container was already deleted
    if (deletionResults.successfulDeletions > 0 || deletionResults.errors['404']) {
      if (sessions.length > 0) {
        this.notifications.success('session.delete.success');
      }
      if (acquisitions.length > 0) {
        this.notifications.success('acquisition.delete.success');
      }
      return deletionResults;
    }
    for (const key in deletionResults.errors) {
      // we currently only delete sessions and acquisitions here, expand this if we're handling more
      this.notifications.error(`${key === 'sessions' ? 'session' : 'acquisition'}.delete.error`);
    }
    return deletionResults;
  }

  async confirmDeleteAll(query: SearchQuery, metadata: ContainerMetadata): Promise<ContainerDeletionResults> {
    const { forbiddenProjects } = await this.getAllowedContainers(metadata, PermissionAction.ContainersDeleteHierarchy);
    let dialogContent = 'Are you sure you want to delete the selected data?';
    if (forbiddenProjects.length > 0) {
      dialogContent = 'You do not have permission to delete data from the following project(s): ' +
        forbiddenProjects.join(', ') + '. Do you want to proceed with deletion?';
    }
    const confirmResult = await this.dialog.open(DeleteDialogComponent, {
      data: { content: dialogContent },
    }).afterClosed().toPromise() as DeleteDialogResult;
    if (!confirmResult.delete) {
      return Promise.resolve(null);
    }
    const deletionResults = await this.deleteAllFromQuery(query, confirmResult.deleteReason ? { delete_reason: confirmResult.deleteReason } : {});
    for (const key in deletionResults.errors) {
      this.notifications.error(`acquisition.removeFile.${key === 'analysis_conflict' ? 'analysisConflict' : 'error'}`);
    }
    if (deletionResults.successfulDeletions > 0) {
      this.notifications.success('session.delete.success');
    }
    return deletionResults;
  }

  /**
   * @return boolean that indicates sessions were moved
   */
  async moveSessionsToProject(metadata: ContainerMetadata): Promise<boolean> {
    const { sessions, forbiddenProjects } = await this.getAllowedContainers(metadata, PermissionAction.ContainersDeleteHierarchy);
    if (sessions.length === 0) {
      await this.showErrorDialog('You do not have the proper permissions to move any of the selected sessions.');
      return Promise.resolve(null);
    }
    const totalSessions = Array.from(this.getCheckedSessions().values()).filter(status => status.checked && !status.indeterminate).length;
    const unmovableSessions = totalSessions - sessions.length;
    if (unmovableSessions > 0) {
      await this.showErrorDialog(`You do not have permission to move ${unmovableSessions} of the selected sessions in the following projects: ${forbiddenProjects.join(', ')}`);
    }
    const dialogClosed$ = this.dialog.open(MoveSessionsToProjectDialog, {
      data: { sessions: sessions.map(sessionId => ({ ...metadata.get(sessionId), _id: sessionId })) },
    }).afterClosed() as Observable<boolean>;
    return firstValueFrom(dialogClosed$);
  }

  /**
   * @return boolean that indicates sessions were moved
   */
  async moveSessionsToSubject(metadata: ContainerMetadata, projectId?: string): Promise<boolean> {
    const { sessions, forbiddenProjects } = await this.getAllowedContainers(metadata, PermissionAction.ContainersDeleteHierarchy);
    if (sessions.length === 0) {
      await this.showErrorDialog('You do not have the proper permissions to move any of the selected sessions.');
      return Promise.resolve(null);
    }
    const totalSessions = Array.from(this.getCheckedSessions().values()).filter(status => status.checked && !status.indeterminate).length;
    const unmovableSessions = totalSessions - sessions.length;
    if (unmovableSessions > 0) {
      await this.showErrorDialog(`You do not have permission to move ${unmovableSessions} of the selected sessions in the following projects: ${forbiddenProjects.join(', ')}`);
    }
    const dialogClosed$ = this.dialog.open(MoveSessionsToSubjectDialog, {
      data: {
        projectId,
        sessions: sessions.map(sessionId => ({ ...metadata.get(sessionId), _id: sessionId })),
      },
    }).afterClosed() as Observable<boolean>;
    return firstValueFrom(dialogClosed$);
  }

  async moveAcquisitionsToSession(metadata: ContainerMetadata): Promise<{ hasMoved: boolean, hasDeleted: boolean }> {
    const { sessions, acquisitions } = await this.getAllowedContainers(metadata, PermissionAction.ContainersDeleteHierarchy);
    if (sessions.length === 0 && acquisitions.length === 0) {
      await this.showErrorDialog('You do not have the proper permissions to move any of the selected acquisitions.');
      return Promise.resolve(null);
    }
    // acquisitions is a partial session selection, sessions is our fully checked session
    // assume only one item is selected for this to run
    let selectedAcquistions: string[] = [];
    if (sessions.length === 1) {
      const sessionAcquisitions = await firstValueFrom(
        this.profileService.siteAdmin$.pipe(
          switchMap(isSiteAdmin => queryAll(() =>
            this.coreApi.sessions.acquisitions(sessions[0], { exhaustive: Boolean(isSiteAdmin) })
          ))
        )
      );
      selectedAcquistions = sessionAcquisitions.map(acq => acq._id);
    } else {
      selectedAcquistions = acquisitions;
    }
    const projectId = Array.from(metadata.values())[0].project;
    const sourceSessionId = Array.from(metadata.keys())[0];
    const dialogClosed$ = this.dialog.open(MoveAcquisitionsToSessionDialog, {
      data: {
        projectId,
        sourceSessionId,
        acquisitions: selectedAcquistions.map(acquisitionId => ({ ...metadata.get(acquisitionId), _id: acquisitionId })),
      },
    }).afterClosed() as Observable<{ hasMoved: boolean, hasDeleted: boolean }>;
    return firstValueFrom(dialogClosed$);
  }

  addCheckedToCollection(): void {
    const sessionIds = [];
    const acquisitionIds = [];
    for (const [sessionId, checkedStatus] of this.getCheckedSessions()) {
      if (!checkedStatus.checked) {
        continue;
      }
      if (checkedStatus.indeterminate) {
        acquisitionIds.push(...Array.from(checkedStatus.acquisitions));
      } else {
        sessionIds.push(sessionId);
      }
    }
    this.dialog.open(AddToCollectionDialog, {
      data: {
        sessions: sessionIds,
        acquisitions: acquisitionIds,
      },
    });
  }

  formattedTimestamp(container: Pick<Session, 'timestamp' | 'timezone'>): string {
    if (isEmpty(container.timestamp)) {
      return '';
    }
    // GNE and product believe that even though we are storing things as UTC, a timestamp without a timezone
    // is NOT the same as an explicit UTC timezone. Don't show the timezone abbreviation or convert to local time
    // if we don't have a timezone set
    if (!this.showTimezone$.value) {
      return formatDate(container.timestamp, 'yyyy-MM-dd HH:mm');
    }
    else if (isEmpty(container.timezone)) {
      // This ensures the timestamp renders without conversion to local timezone
      return timestampToLocaltime(container.timestamp, 'UTC', 'yyyy-MM-dd HH:mm');
    }
    return timestampToLocaltime(container.timestamp, container.timezone);
  }

  missingTimezoneMessage(session: Session): string | null {
    return isEmpty(session.timezone) && this.showTimezone$.value ? 'No timezone saved for this timestamp' : null;
  }

  reloadCurrentSessions(): void {
    if (!this.lastSessionPageParams) {
      return;
    }
    this.getSessionPage(this.lastSessionPageParams);
  }
}

export interface SessionCheckedStatus {
  _id: string
  checked: boolean
  indeterminate: boolean
  acquisitions: Set<string>
}

export interface SubjectCheckedStatus {
  _id: string
  checked: boolean
  indeterminate: boolean
}

export interface AcquisitionUpdate {
  session: Partial<Session>
  acquisitionIds: string[]
}

export interface ContainerDeletionCriteria {
  containers: { _id: string }[];
  updateKey: string;
}

export interface ContainerDeletionResults {
  successfulDeletions: number
  // Keys in the errors map are container type, value is the bulk delete error
  errors: { [key: string]: Error }
}

export type SessionMetadata = Map<string, Pick<Session, 'label' | 'project' | 'timestamp'>>;
export type SubjectMetadata = Map<string, Pick<Subject, 'label' | 'project'>>;
export type ContainerMetadata = SessionMetadata | SubjectMetadata;

interface AllowedContainers {
  acquisitions: string[]
  sessions?: string[]
  subjects?: string[]
  forbiddenProjects: string[]
}
